import {FC, useCallback, useEffect, useState} from 'react';

import PinDropIcon from '@mui/icons-material/PinDrop';
import {Box, Grid2, Link, styled, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

import Button from 'components/button/Button';
import {config} from 'config/env';
import {useAppSelector} from 'hooks';
import {useAppMode} from 'hooks/useAppMode';
import SendBrowserEvent from 'services/send-browser-event';
import {TPinMapSelectedLocation} from 'types';
import {isSubdistrictMatch} from 'utils';
import {extractGoogleAddressComponents} from 'utils/googleMapHelper';
import {getCurrentLocation} from 'utils/location';
import {MapWrapper} from 'views/map-wrapper/MapWrapper';

import {MapButtonContainer, StyledMapWrapperGrid2} from './AddressFullMapView';

type PinMapViewProps = {
  setFullMapView?: (arg: boolean) => void;
  addressString?: string;
  onPinSubmit: (selectedLocationFromGoogle: google.maps.GeocoderResult | undefined) => void;
  onLocationChange: (lat: number, lng: number) => void;
  onZoomChange: (zoomLevel: number) => void;
  isAddressFormOpen: boolean;
  loading: boolean;
  selectedLocation?: TPinMapSelectedLocation;
  initialPinMapData?: TPinMapSelectedLocation;
  setHasUserMovedMapManually: (value: boolean) => void;
};

const StyledLink = styled(Link)(() => ({
  fontWeight: 400,
  textDecoration: 'underline',
  fontSize: '14px',
  cursor: 'pointer'
}));

export const PinMapView: FC<PinMapViewProps> = ({
  addressString,
  onPinSubmit,
  onLocationChange,
  isAddressFormOpen,
  loading,
  onZoomChange,
  selectedLocation,
  initialPinMapData,
  setHasUserMovedMapManually
}) => {
  const {t} = useTranslation();

  const isMapViewGeocodingEnabled = config.IS_MAPVIEW_GEOCODING_ENABLED;
  const isPinDisabledForGoogleMismatch = config.IS_PIN_DISABLED_FOR_GOOGLE_MISMATCH;
  const isEditPinButtonHidden = config.IS_EDIT_PIN_BUTTON_HIDDEN;

  const isPinMapViewOnTop = !isAddressFormOpen;

  const [geoCodedAddress, setGeoCodedAddress] = useState<google.maps.GeocoderResult>();
  const [hasUserMovedMap, setHasUserMovedMap] = useState<boolean>(false);
  const [geolocationEnabled, setGeolocationEnabled] = useState(false);

  const [isGeoCoding, setIsGeoCoding] = useState(false);

  const {currentStore} = useAppSelector((state) => state?.app);

  const [notSupported, setNotSupported] = useState(false);
  const {isExpress} = useAppMode();

  let formattedAddress = geoCodedAddress?.formatted_address;

  useEffect(() => {
    async function checkLocationPermission() {
      try {
        await getCurrentLocation();
        setGeolocationEnabled(true);
      } catch (err) {
        setGeolocationEnabled(false);
      }
    }
    checkLocationPermission();
  }, []);

  if (!formattedAddress && selectedLocation) {
    if (selectedLocation?.address) {
      formattedAddress = `${selectedLocation?.address}, ${selectedLocation?.postalCode}`;
    } else {
      formattedAddress = addressString;
    }
  }

  const _onPinSubmit = () => {
    const googleAddress = extractGoogleAddressComponents(geoCodedAddress as google.maps.GeocoderResult);

    if (
      isPinDisabledForGoogleMismatch &&
      isExpress &&
      // adding the default missing-{source}-value to prevent undefined === undefined case
      (initialPinMapData
        ? googleAddress?.postalCode !== initialPinMapData?.postalCode ||
          !isSubdistrictMatch(
            googleAddress?.subdistrict || 'missing-google-subdistrict',
            initialPinMapData?.subdistrictEn || 'missing-initial-subdistrict'
          )
        : googleAddress?.postalCode !== currentStore?.zipcode ||
          !isSubdistrictMatch(
            googleAddress?.subdistrict || 'missing-google-subdistrict',
            currentStore?.subdistrictEn || 'missing-current-subdistrict'
          ))
    ) {
      setNotSupported(true);
      return;
    } else {
      setNotSupported(false);
    }

    if (isGeoCoding) return;
    SendBrowserEvent.showSuccessToast(t('message.pinSavedSucces'));

    if (config.IS_GOOGLE_ZIP_ENABLED) {
      onPinSubmit(geoCodedAddress);
    } else {
      onPinSubmit(undefined);
    }
  };

  const _setGeoCodedAddress = useCallback((addr: google.maps.GeocoderResult) => {
    setGeoCodedAddress(addr);
  }, []);

  return (
    <Grid2 container alignItems="flex-start">
      <StyledMapWrapperGrid2>
        <MapWrapper
          latLng={selectedLocation?.coordinates}
          addressString={addressString}
          onClick={({lat, lng}) => {
            setNotSupported(false);
            !hasUserMovedMap && setHasUserMovedMap(true);

            onLocationChange(lat(), lng());
          }}
          markerPinAllowed={true}
          isToolTipVisible={isPinMapViewOnTop}
          setGeoCodedAddress={_setGeoCodedAddress}
          initialZoomLevel={selectedLocation?.zoomLevel}
          onZoomChange={(zoom: number) => {
            onZoomChange(zoom);
          }}
          updateLoadingState={setIsGeoCoding}
          setHasUserMovedMapManually={setHasUserMovedMapManually}
        />
        {/* Tooltip Overlay */}
      </StyledMapWrapperGrid2>
      <Grid2 size={{xs: 12}} sx={{backgroundColor: 'red'}}>
        <MapButtonContainer>
          {isMapViewGeocodingEnabled && !!formattedAddress && (
            <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" pb={2} px={1}>
              <Box flexDirection="row" display="flex" alignItems="center">
                <PinDropIcon sx={{color: '#c6c6c6'}} />
                <Typography pl={1}>{formattedAddress}</Typography>
              </Box>
              {/* hide this button behind flag */}
              {isEditPinButtonHidden ? null : (
                <StyledLink variant="body2" onClick={_onPinSubmit}>
                  {t('button.edit')}
                </StyledLink>
              )}
            </Box>
          )}
          <Button
            fullWidth
            shape="rounded"
            disabled={
              isMapViewGeocodingEnabled
                ? !formattedAddress || isGeoCoding || notSupported || (!geolocationEnabled && !hasUserMovedMap)
                : false
            }
            variant="contained"
            loading={loading || isGeoCoding}
            onClick={_onPinSubmit}
          >
            {t(notSupported ? 'express.title.unavailableAddresses' : 'button.useLocation')}
          </Button>
        </MapButtonContainer>
      </Grid2>
    </Grid2>
  );
};
